/**
 * XACML Serializer Utility
 * Converts structured policy format back to XACML 3.0 JSON
 */

import type {
  ParsedXacmlPolicy,
  ParsedXacmlAttributes,
  ParsedXacmlAttribute,
  ParsedXacmlRule,
  ParsedXacmlCondition,
  ParsedXacmlObligation,
  XacmlCategory,
} from '@/types/policy'
import { XACML_CATEGORY_URNS } from './xacml-parser'

/**
 * Standard XACML URN prefixes per category.
 * Used to expand short attribute keys (e.g., "subject-id") to full URNs
 * (e.g., "urn:oasis:names:tc:xacml:1.0:subject:subject-id") during serialization.
 */
const CATEGORY_ATTRIBUTE_URN_PREFIXES: Record<string, string> = {
  'urn:oasis:names:tc:xacml:1.0:subject-category:access-subject': 'urn:oasis:names:tc:xacml:1.0:subject:',
  'urn:oasis:names:tc:xacml:3.0:attribute-category:resource': 'urn:oasis:names:tc:xacml:1.0:resource:',
  'urn:oasis:names:tc:xacml:3.0:attribute-category:action': 'urn:oasis:names:tc:xacml:1.0:action:',
  'urn:oasis:names:tc:xacml:3.0:attribute-category:environment': 'urn:oasis:names:tc:xacml:3.0:environment:',
}

/**
 * Combining algorithm shorthand → full XACML URN mapping.
 */
const COMBINING_ALG_URNS: Record<string, string> = {
  'deny-overrides': 'urn:oasis:names:tc:xacml:3.0:rule-combining-algorithm:deny-overrides',
  'permit-overrides': 'urn:oasis:names:tc:xacml:3.0:rule-combining-algorithm:permit-overrides',
  'first-applicable': 'urn:oasis:names:tc:xacml:1.0:rule-combining-algorithm:first-applicable',
  'only-one-applicable': 'urn:oasis:names:tc:xacml:1.0:rule-combining-algorithm:only-one-applicable',
  'deny-unless-permit': 'urn:oasis:names:tc:xacml:3.0:rule-combining-algorithm:deny-unless-permit',
  'permit-unless-deny': 'urn:oasis:names:tc:xacml:3.0:rule-combining-algorithm:permit-unless-deny',
}

/**
 * Normalize a combining algorithm to a full XACML URN.
 * If already a full URN, passes through unchanged.
 */
function normalizeCombiningAlgorithm(alg: string): string {
  if (alg.startsWith('urn:')) {
    return alg
  }
  return COMBINING_ALG_URNS[alg] ?? alg
}

/**
 * Normalize an attribute ID to a full XACML URN.
 * If the attribute ID is already a full URN, it passes through unchanged.
 * Short keys are expanded using the category's standard URN prefix.
 */
function normalizeAttributeId(attributeId: string, categoryUrn: string): string {
  if (attributeId.startsWith('urn:') || attributeId.startsWith('http:')) {
    return attributeId
  }
  const prefix = CATEGORY_ATTRIBUTE_URN_PREFIXES[categoryUrn]
  if (prefix) {
    return `${prefix}${attributeId}`
  }
  return attributeId
}

/**
 * XACML Match structure
 */
interface XacmlMatch {
  MatchId: string
  AttributeDesignator: {
    Category: string
    AttributeId: string
    DataType: string
    MustBePresent: boolean
  }
  AttributeValue: {
    DataType: string
    Value: string | number | boolean
  }
}

/**
 * XACML Target structure
 */
interface XacmlTarget {
  AnyOf: Array<{
    AllOf: Array<{
      Match: XacmlMatch[]
    }>
  }>
}

/**
 * Recursive XACML Apply node supporting nested structures like not(comparison(...))
 */
interface XacmlApplyNode {
  FunctionId: string
  Apply?: XacmlApplyNode[]
  AttributeDesignator?: {
    Category: string
    AttributeId: string
    DataType: string
    MustBePresent: boolean
  }
  AttributeValue?: {
    DataType: string
    Value: string | number | boolean
  }
}

/**
 * XACML Condition structure
 */
interface XacmlCondition {
  Apply: XacmlApplyNode
}

/**
 * XACML Obligation Expression structure
 */
interface XacmlObligationExpression {
  ObligationId: string
  FulfillOn: 'Permit' | 'Deny'
  AttributeAssignment?: Array<{
    AttributeId: string
    Value: string | number | boolean
    DataType?: string
  }>
}

/**
 * XACML Rule structure
 */
interface XacmlRule {
  RuleId: string
  Effect: 'Permit' | 'Deny'
  Description?: string
  Target?: XacmlTarget
  Condition?: XacmlCondition
  ObligationExpressions?: XacmlObligationExpression[]
}

/**
 * XACML Policy structure
 */
interface XacmlPolicy {
  PolicyId: string
  Version: string
  RuleCombiningAlgId: string
  Description?: string
  Target?: XacmlTarget
  Rule: XacmlRule[]
}

/**
 * Default match function for data type
 */
function getDefaultMatchFunction(dataType: string): string {
  if (dataType.includes('string')) {
    return 'urn:oasis:names:tc:xacml:1.0:function:string-equal'
  }
  if (dataType.includes('integer')) {
    return 'urn:oasis:names:tc:xacml:1.0:function:integer-equal'
  }
  if (dataType.includes('boolean')) {
    return 'urn:oasis:names:tc:xacml:1.0:function:boolean-equal'
  }
  if (dataType.includes('date')) {
    return 'urn:oasis:names:tc:xacml:1.0:function:date-equal'
  }
  if (dataType.includes('time')) {
    return 'urn:oasis:names:tc:xacml:1.0:function:time-equal'
  }
  return 'urn:oasis:names:tc:xacml:1.0:function:string-equal'
}

/**
 * Serialize a single attribute to XACML Match array
 */
function serializeAttribute(
  attribute: ParsedXacmlAttribute,
  category: XacmlCategory
): XacmlMatch[] {
  const categoryUrn = XACML_CATEGORY_URNS[category]
  const matchId = attribute.matchId ?? getDefaultMatchFunction(attribute.dataType)

  return attribute.values.map((value) => ({
    MatchId: matchId,
    AttributeDesignator: {
      Category: categoryUrn,
      AttributeId: normalizeAttributeId(attribute.attributeId, categoryUrn),
      DataType: attribute.dataType,
      MustBePresent: true,
    },
    AttributeValue: {
      DataType: attribute.dataType,
      Value: value,
    },
  }))
}

/**
 * Serialize parsed attributes to XACML Target
 */
export function serializeTarget(
  attributes: ParsedXacmlAttributes
): XacmlTarget | undefined {
  const categories: XacmlCategory[] = ['subject', 'resource', 'action', 'environment']
  const allMatches: XacmlMatch[] = []

  for (const category of categories) {
    const categoryAttrs = attributes[category]
    for (const attr of categoryAttrs) {
      if (attr.attributeId && attr.values.length > 0) {
        allMatches.push(...serializeAttribute(attr, category))
      }
    }
  }

  if (allMatches.length === 0) {
    return undefined
  }

  // Group matches into a single AnyOf with single AllOf
  // This creates an AND relationship between all matches
  return {
    AnyOf: [
      {
        AllOf: [
          {
            Match: allMatches,
          },
        ],
      },
    ],
  }
}

/**
 * Get one-and-only function for data type
 */
function getOneAndOnlyFunction(dataType: string): string {
  if (dataType.includes('string')) {
    return 'urn:oasis:names:tc:xacml:1.0:function:string-one-and-only'
  }
  if (dataType.includes('integer')) {
    return 'urn:oasis:names:tc:xacml:1.0:function:integer-one-and-only'
  }
  if (dataType.includes('boolean')) {
    return 'urn:oasis:names:tc:xacml:1.0:function:boolean-one-and-only'
  }
  if (dataType.includes('date')) {
    return 'urn:oasis:names:tc:xacml:1.0:function:date-one-and-only'
  }
  if (dataType.includes('time')) {
    return 'urn:oasis:names:tc:xacml:1.0:function:time-one-and-only'
  }
  return 'urn:oasis:names:tc:xacml:1.0:function:string-one-and-only'
}

/**
 * Get category URN for attribute ID (heuristic based on common patterns)
 */
function getCategoryForAttribute(attributeId: string): string {
  const id = attributeId.toLowerCase()

  if (id.includes('subject') || id.includes('user') || id.includes('role')) {
    return XACML_CATEGORY_URNS.subject
  }
  if (id.includes('resource') || id.includes('document') || id.includes('file')) {
    return XACML_CATEGORY_URNS.resource
  }
  if (id.includes('action') || id.includes('operation')) {
    return XACML_CATEGORY_URNS.action
  }
  if (id.includes('environment') || id.includes('time') || id.includes('date') || id.includes('ip')) {
    return XACML_CATEGORY_URNS.environment
  }

  // Default to resource for unknown attributes
  return XACML_CATEGORY_URNS.resource
}

/**
 * Serialize a single condition to XACML Condition.
 * When condition.negated is true, wraps the comparison Apply in a not() Apply.
 */
function serializeCondition(condition: ParsedXacmlCondition): XacmlCondition {
  const category = condition.category
    ? XACML_CATEGORY_URNS[condition.category]
    : getCategoryForAttribute(condition.attributeId)

  const comparisonApply: XacmlApplyNode = {
    FunctionId: condition.functionId,
    Apply: [
      {
        FunctionId: getOneAndOnlyFunction(condition.dataType),
        AttributeDesignator: {
          Category: category,
          AttributeId: normalizeAttributeId(condition.attributeId, category),
          DataType: condition.dataType,
          MustBePresent: true,
        },
      },
    ],
    AttributeValue: {
      DataType: condition.dataType,
      Value: condition.value,
    },
  }

  if (condition.negated) {
    return {
      Apply: {
        FunctionId: 'urn:oasis:names:tc:xacml:1.0:function:not',
        Apply: [comparisonApply],
      },
    }
  }

  return { Apply: comparisonApply }
}

/**
 * Serialize multiple conditions with AND logic
 */
function serializeConditions(conditions: ParsedXacmlCondition[]): XacmlCondition | undefined {
  if (conditions.length === 0) {
    return undefined
  }

  if (conditions.length === 1) {
    return serializeCondition(conditions[0])
  }

  // Multiple conditions - wrap in and() Apply
  return {
    Apply: {
      FunctionId: 'urn:oasis:names:tc:xacml:1.0:function:and',
      Apply: conditions.map((c) => serializeCondition(c).Apply),
    },
  }
}

/**
 * Serialize obligations to XACML ObligationExpressions
 */
function serializeObligations(obligations: ParsedXacmlObligation[]): XacmlObligationExpression[] {
  return obligations.map((obligation) => {
    const expr: XacmlObligationExpression = {
      ObligationId: obligation.obligationId,
      FulfillOn: obligation.fulfillOn,
    }

    if (obligation.attributeAssignments.length > 0) {
      expr.AttributeAssignment = obligation.attributeAssignments.map((aa) => ({
        AttributeId: aa.attributeId,
        Value: aa.value,
        ...(aa.dataType ? { DataType: aa.dataType } : {}),
      }))
    }

    return expr
  })
}

/**
 * Serialize a parsed rule to XACML Rule
 */
export function serializeRule(rule: ParsedXacmlRule): XacmlRule {
  const xacmlRule: XacmlRule = {
    RuleId: rule.ruleId,
    Effect: rule.effect,
  }

  if (rule.description) {
    xacmlRule.Description = rule.description
  }

  if (rule.target) {
    const target = serializeTarget(rule.target)
    if (target) {
      xacmlRule.Target = target
    }
  }

  if (rule.rawCondition) {
    // Preserve original condition structure to avoid lossy round-trip serialization.
    // The parser/serializer cannot faithfully reconstruct nested Apply trees
    // (e.g., not(string-equal(...))), so we use the raw JSON when available.
    xacmlRule.Condition = rule.rawCondition as unknown as XacmlCondition
  } else if (rule.conditions.length > 0) {
    const condition = serializeConditions(rule.conditions)
    if (condition) {
      xacmlRule.Condition = condition
    }
  }

  if (rule.obligations && rule.obligations.length > 0) {
    // Prefer the raw snapshot only when the editor has not touched obligations.
    // Obligation add/edit/remove clears rawObligationExpressions (same pattern as
    // rawCondition). Do NOT use a length heuristic — in-place edits (mask type,
    // message text, column name) keep the same count and would otherwise ship
    // the original values in the PUT payload.
    if (rule.rawObligationExpressions) {
      xacmlRule.ObligationExpressions =
        rule.rawObligationExpressions as unknown as XacmlObligationExpression[]
    } else {
      xacmlRule.ObligationExpressions = serializeObligations(rule.obligations)
    }
  }

  return xacmlRule
}

/**
 * Serialize parsed rules array to XACML Rules array
 */
export function serializeRules(rules: ParsedXacmlRule[]): XacmlRule[] {
  return rules.map(serializeRule)
}

/**
 * Serialize a full parsed policy to XACML Policy JSON
 */
export function serializeToXacml(policy: ParsedXacmlPolicy): XacmlPolicy {
  const xacmlPolicy: XacmlPolicy = {
    PolicyId: policy.policyId,
    Version: policy.version || '1.0',
    RuleCombiningAlgId: normalizeCombiningAlgorithm(
      policy.ruleCombiningAlgorithm ||
      'urn:oasis:names:tc:xacml:3.0:rule-combining-algorithm:deny-overrides'
    ),
    Rule: serializeRules(policy.rules),
  }

  if (policy.description) {
    xacmlPolicy.Description = policy.description
  }

  const target = serializeTarget(policy.target)
  if (target) {
    xacmlPolicy.Target = target
  }

  return xacmlPolicy
}

/**
 * Create empty parsed policy structure
 */
export function createEmptyPolicy(): ParsedXacmlPolicy {
  return {
    policyId: 'new-policy',
    version: '1.0',
    description: '',
    ruleCombiningAlgorithm: 'urn:oasis:names:tc:xacml:3.0:rule-combining-algorithm:deny-overrides',
    target: {
      subject: [],
      resource: [],
      action: [],
      environment: [],
    },
    rules: [],
  }
}

/**
 * Merge existing XACML content with structured edits
 * Preserves fields not covered by the structured editor
 */
export function mergeWithExistingPolicy(
  existingContent: Record<string, unknown> | null,
  parsedPolicy: ParsedXacmlPolicy
): Record<string, unknown> {
  const serialized = serializeToXacml(parsedPolicy)

  if (!existingContent) {
    return serialized as unknown as Record<string, unknown>
  }

  // Merge, preserving any extra fields from existing content
  return {
    ...existingContent,
    ...serialized,
  }
}
